import React, { useState, useRef } from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import {
  PrimaryButton,
  SecondaryButton,
  ButtonSizeOptions,
  ImageLoader
} from "@loanbossinc/component-library";
import Divider from "@material-ui/core/Divider";
import ContextualMenu, { MenuAlignmentMode } from "components/Global/ContextualMenu";
import { useFlags } from "launchdarkly-react-client-sdk";
import "./styles.css";

const StyledImageHeader = styled.div`
  && {
    height: 30px;
    width: 30px;
    margin: 0.2em;
    margin-bottom: 0.4em;
    display: inline-block;
    vertical-align: top !important;
  }
`;

const StyledImageLoader = styled(ImageLoader)`
  && {
    height: 30px;
    width: 30px;
    margin: 0.2em;
  }
`;

const StyledWrapper = styled.div`
  && {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 24px;
  }
`;

const StyledImage = styled.div`
  && {
    display: inline;
    img:not(:first-child) {
      margin-left: 4px;
    }
  }
`;

const StyledHeader = styled.div`
  && {
    display: flex;
    flex-direction: column;
    flex: 1;
    font-family: Work Sans;
    font-weight: 500;
    font-size: 20px;
    justify-content: center;

    span:not(:first-child) {
      font-weight: 400;
      font-size: 14px;
    }
  }
`;

const LoanHeader = (props) => {
  const { loan, authentication, history } = props;
  const {
    us1304BrokerFrontendUrlEnvironmentVariable: brokerFrontendURL,
    gla321CashflowsAdminButtonCompanies: showCashflowsAdminButtonForUsersArray,
    gla321EnhancedCashflowsButtonCompanies: showEnhancedCashflowsButtonCompanyIds,
    bar1209ReserveButtonCompanies: showReserveButtonForUsersCompanyIds,
    bad138RemoveAccessToLegacyCashflows: removeAccessToLegacyCashflows
  } = useFlags();
  const companyId = authentication.user.contact.companyId;
  const showEnhancedCashflowsButton = showEnhancedCashflowsButtonCompanyIds.includes(companyId);
  const showReserveButtonForUsers = showReserveButtonForUsersCompanyIds.includes(companyId);
  const showCashflowsAdminButtonForUsers = showCashflowsAdminButtonForUsersArray.length > 0;
  const [isCashflowsNavigationmenuPresented, setIsCashflowsNavigationmenuPresented] = useState(false);
  const cashflowsMenuPresentationWrapper = useRef();

  const getHeaderImages = () => {
    const { details } = loan;
    if (details.loanProvider && details.loanProvider.length > 0) {
      return details.loanProvider.reduce((accum, cur, index) => {
        if (!cur || !cur.name) return accum;
        accum.push(
          <StyledImageHeader key={index}>
            <StyledImageLoader
              src={cur.logoImage}
              defaultImage={
                process.env.PUBLIC_URL + "/images/default-lender-icon.svg"
              }
              title={cur.name}
              key={index}
            />
          </StyledImageHeader>
        );
        return accum;
      }, []);
    }
    return [];
  };

  const renderTitleHeading = () => {
    if (loan && loan.details && loan.details.loanSeniority) {
      const loanSeniority = loan.details.loanSeniority.value;
      const closingDate = loan.details.closingDate ? moment(loan.details.closingDate).format("YYYY") : "";
      return <span>{ `${loanSeniority} ${closingDate}` }</span>;
    }
    return <span>Loan</span>;
  };

  const renderSubtitleHeading = () => {
    if (!loan) {
      return null;
    }
    const { details } = loan;
    if (details.loanProvider && details.loanProvider.length > 0) {
      const lenderNames = details.loanProvider
        .reduce((accum, cur) => {
          if (!cur || !cur.name) return accum;
          accum.push(cur.name);
          return accum;
        }, [])
        .join(", ");
      return <span>{ lenderNames.length > 60 ? lenderNames.slice(0, 60) + "…" : lenderNames }</span>;
    }
    return null;
  };

  const renderButtonContainer = () => {
    const cashflowsNavigationMenuItems = [
      { title: "Enhanced Cashflows", onClick: navigateToEnhancedCashflows },
      { title: "Legacy Cashflows", onClick: navigateToLegacyCashflows }
    ];

    return (
      <div className="header-buttons">
        { (isUserSystemAdmin || showCashflowsAdminButtonForUsers) && (
          <SecondaryButton
            size={ButtonSizeOptions.smFluid}
            label="Cashflows Admin"
            onClick={navigateToCashflowsAdmin}
         />
        )}
        <div className="menu-button" ref={cashflowsMenuPresentationWrapper}>
          <PrimaryButton
            size={ButtonSizeOptions.smFluid}
            label="Cashflows"
            onClick={onCashflowsClick}
          />
          { isCashflowsNavigationmenuPresented &&
            <ContextualMenu
              presentationElement={cashflowsMenuPresentationWrapper.current}
              alignmentMode={MenuAlignmentMode.TopTrailing}
              menuItems={cashflowsNavigationMenuItems}
              onDismiss={() => {
                setIsCashflowsNavigationmenuPresented(false);
              }}
            />
          }
        </div>
        { (isUserSystemAdmin || showReserveButtonForUsers) && (
          <SecondaryButton
            size={ButtonSizeOptions.smFluid}
            label="Reserves"
            onClick={navigateToReserve}
         />
        )}
      </div>
    );
  };

  const navigateToLegacyCashflows = () => {
    const { dealId, id } = loan.details;
    history.push(`/portfolio/funds/free/deals/${dealId}/cashflowsPage?showActiveLoans=true&loanId=${id}`);
  };

  const navigateToEnhancedCashflows = () => {
    const { dealId, id } = loan.details;
    window.location.assign(`${ brokerFrontendURL }/cashflows/${dealId}/loans/${id}`);
  };

  const navigateToCashflowsAdmin = async () => {
    const { dealId, id } = loan.details;
    window.location.assign(`${ brokerFrontendURL }/borrower-deals/${dealId}/loan/${id}`);
  };

  const navigateToReserve = () => {
    const { dealId } = loan.details;
    window.location.assign(`${ brokerFrontendURL }/escrows/${dealId}/reserve-account`);
  };

  const presentCashflowsNavigationMenu = () => {
    if (cashflowsMenuPresentationWrapper.current) {
      setIsCashflowsNavigationmenuPresented(true);
    }
  };

  const isUserSystemAdmin = authentication.isSystemAdmin;
  const shouldOfferCashflowsRouteMenu = isUserSystemAdmin || showEnhancedCashflowsButton;

  const onCashflowsClick = () => {
    if (removeAccessToLegacyCashflows) {
      navigateToEnhancedCashflows();
      return;
    }

    if (removeAccessToLegacyCashflows) {
      navigateToEnhancedCashflows();
    } else {
      presentCashflowsNavigationMenu();
    }
  };

  return (
    <>
      <StyledWrapper>
        <StyledImage> {getHeaderImages()} </StyledImage>

        <div style={{ display: "flex" }}>
          <StyledHeader>
            { renderTitleHeading() }
            { renderSubtitleHeading() }
          </StyledHeader>
          { renderButtonContainer() }
        </div>
      </StyledWrapper>

      <Divider style={{ margin: "0" }} variant="inset" />
    </>
  );
};

export default withRouter(LoanHeader);
